import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths } from "date-fns";
import { it as itLocale, enUS, fr as frLocale, es as esLocale } from "date-fns/locale";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import LanguageSelector from "./i18n/LanguageSelector";
import {
  Building2,
  Zap,
  Wrench,
  Users,
  Home,
  Plus,
  Pencil,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Euro,
  Plug,
  Power,
  TrendingUp,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Settings,
  BarChart3,
  Search,
  Filter,
  Eye,
  MessageCircle,
  Send,
  Bot,
  FileText,
  Upload,
  Download,
  ExternalLink,
  HelpCircle,
  Loader2,
  Ticket,
  QrCode,
  Printer,
  Star,
  Bell,
  Lightbulb,
  Map,
  Thermometer,
  Activity,
  LogOut,
  Shield,
  Navigation,
  Box,
  Video,
  RefreshCw,
  Car,
  Package,
  Sparkles,
  Calculator,
  Coins,
  Globe
} from "lucide-react";
import { LoginPage, UserManagement } from "./AuthPage";
import { TicketList, CalendarioManutenzioni, QRCodeDialog, formatDateIT } from "./TicketCalendarQR";
import { PlanimetriaEditor, SuggerimentiProattivi, NotificationBadge } from "./PlanimetriaSuggerimenti";
import SmartBuildingDashboard from "./SmartBuildingDashboard";
import Inventario from "./Inventario";
import Dipendenti from "./Dipendenti";
import DesignAssistant from "./DesignAssistant";
import ComputoMetrico from "./ComputoMetrico";
import Panorami360 from "./Panorami360";
import { WalletWidget, WalletRechargeDialog, WalletAdminPanel } from "./Wallet";
import VideoCameraManager from "./VideoCameraManager";
import MatterportManager from "./MatterportManager";
import ElettrodomesticoDialog from "./ElettrodomesticoForm";
import RicercaCentriAssistenza from "./RicercaCentriAssistenza";
import PropertyConfig from "./PropertyConfig";
import SensorReport from "./SensorReport";
import VehicleTracker from "./VehicleTracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories and Enums
const CATEGORIE_ELETTRODOMESTICI = [
  { value: "cucina", label: "Cucina", icon: "🍳" },
  { value: "lavanderia", label: "Lavanderia", icon: "🧺" },
  { value: "climatizzazione", label: "Climatizzazione", icon: "❄️" },
  { value: "intrattenimento", label: "Intrattenimento", icon: "📺" },
  { value: "illuminazione", label: "Illuminazione", icon: "💡" },
  { value: "pulizia", label: "Pulizia", icon: "🧹" },
  { value: "sicurezza", label: "Sicurezza", icon: "🔒" },
  { value: "altro", label: "Altro", icon: "📦" },
];

const SMART_PLUG_PROVIDERS = [
  { value: "nessuno", label: "Nessuno" },
  { value: "smartthings", label: "SmartThings" },
  { value: "tuya", label: "Tuya" },
  { value: "shelly", label: "Shelly" },
  { value: "tapo", label: "TP-Link Tapo" },
  { value: "meross", label: "Meross" },
  { value: "altro", label: "Altro" },
];

// Stati UNIFICATI per Ticket e Manutenzioni  
const STATI_UNIFICATI = [
  { value: "aperto", label: "Aperto", labelManutenzione: "Pianificato", color: "bg-blue-500", icon: "📋" },
  { value: "contattato", label: "Contattato", labelManutenzione: "Contattato", color: "bg-yellow-500", icon: "📞" },
  { value: "in_lavorazione", label: "In Lavorazione", labelManutenzione: "In Corso", color: "bg-orange-500", icon: "🔧" },
  { value: "completato", label: "Completato", labelManutenzione: "Completato", color: "bg-green-500", icon: "✅" },
  { value: "annullato", label: "Annullato", labelManutenzione: "Annullato", color: "bg-gray-500", icon: "❌" },
];

// Alias per retrocompatibilità frontend
const STATI_MANUTENZIONE = STATI_UNIFICATI;

const TIPI_MANUTENZIONE = [
  { value: "ordinaria", label: "Ordinaria" },
  { value: "straordinaria", label: "Straordinaria" },
  { value: "riparazione", label: "Riparazione" },
  { value: "controllo", label: "Controllo" },
  { value: "pulizia", label: "Pulizia" },
  { value: "sostituzione", label: "Sostituzione" },
];

const STATI_TICKET = [
  { value: "aperto", label: "Aperto", color: "bg-blue-500" },
  { value: "contattato", label: "Contattato", color: "bg-yellow-500" },
  { value: "in_lavorazione", label: "In Lavorazione", color: "bg-orange-500" },
  { value: "risolto", label: "Risolto", color: "bg-green-500" },
  { value: "annullato", label: "Annullato", color: "bg-gray-500" },
];

const PRIORITA_TICKET = [
  { value: "bassa", label: "Bassa", color: "bg-gray-400" },
  { value: "media", label: "Media", color: "bg-blue-400" },
  { value: "alta", label: "Alta", color: "bg-orange-500" },
  { value: "urgente", label: "Urgente", color: "bg-red-500" },
];

// Matterport Viewer Component
// AI Assistant Component
const AssistenteAI = ({ elettrodomestici, onNavigateToElettrodomestico, onOpenTicket, onNavigateToTab, authToken }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedElettrodomestico, setSelectedElettrodomestico] = useState("");
  const [showProblemSolver, setShowProblemSolver] = useState(false);
  const [problema, setProblema] = useState("");
  const [soluzione, setSoluzione] = useState(null);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const messagesEndRef = { current: null };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = { role: "user", content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/assistente/chat`, {
        message: inputMessage,
        conversation_history: messages.slice(-10),
        elettrodomestico_id: selectedElettrodomestico || null,
        token: authToken
      });

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        sources: response.data.sources,
        suggested_actions: response.data.suggested_actions
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Errore chat:", error);
      toast.error("Errore nella comunicazione con l'assistente");
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Mi dispiace, c'è stato un errore. Riprova tra poco."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const risolviProblema = async () => {
    if (!selectedElettrodomestico || !problema.trim()) {
      toast.error("Seleziona un elettrodomestico e descrivi il problema");
      return;
    }

    setIsLoading(true);
    setSoluzione(null);
    setShowActionButtons(false);

    try {
      const response = await axios.post(
        `${API}/assistente/risolvi-problema?elettrodomestico_id=${selectedElettrodomestico}&problema=${encodeURIComponent(problema)}&token=${authToken}`
      );
      setSoluzione(response.data);
      // Mostra i pulsanti azione dopo 2 secondi dalla soluzione
      setTimeout(() => setShowActionButtons(true), 2000);
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore nella risoluzione del problema");
      setShowActionButtons(true); // Mostra pulsanti anche in caso di errore
    } finally {
      setIsLoading(false);
    }
  };

  // Funzione per aprire un ticket precompilato
  const handleApriTicket = () => {
    const elettro = elettrodomestici.find(e => e.id === selectedElettrodomestico);
    if (onOpenTicket) {
      onOpenTicket({
        elettrodomestico_id: selectedElettrodomestico,
        titolo: `Problema: ${problema.substring(0, 50)}${problema.length > 50 ? '...' : ''}`,
        descrizione: `Problema segnalato:\n${problema}\n\n${soluzione ? `Soluzione tentata:\n${soluzione.soluzione?.substring(0, 500) || 'N/A'}` : ''}`,
        elettrodomestico: elettro
      });
    }
    toast.success("Apertura form ticket...");
  };

  // Funzione per contattare assistenza
  const handleContattaAssistenza = () => {
    const elettro = elettrodomestici.find(e => e.id === selectedElettrodomestico);
    if (elettro?.centro_assistenza_id) {
      // Naviga alla tab centri assistenza
      if (onNavigateToTab) {
        onNavigateToTab("centri");
      }
      toast.success("Vai alla sezione Centri Assistenza");
    } else {
      toast.info("Nessun centro assistenza collegato. Vai alla sezione Centri per cercarne uno.");
      if (onNavigateToTab) {
        onNavigateToTab("centri");
      }
    }
  };

  // Funzione per navigare nello spazio 3D
  const handleVaiNelloSpazio = () => {
    const elettro = elettrodomestici.find(e => e.id === selectedElettrodomestico);
    if (elettro?.matterport_tag_id) {
      if (onNavigateToElettrodomestico) {
        onNavigateToElettrodomestico(selectedElettrodomestico);
      }
      toast.success("Navigazione verso l'elettrodomestico nel modello 3D...");
    } else {
      // Naviga comunque alla vista 3D
      if (onNavigateToTab) {
        onNavigateToTab("matterport");
      }
      toast.info("Vai alla Vista 3D per localizzare l'elettrodomestico");
    }
  };

  const cercaManuale = async (marca, modello) => {
    try {
      const response = await axios.get(`${API}/ricerca-manuale`, {
        params: { marca, modello }
      });
      return response.data;
    } catch (error) {
      console.error("Errore ricerca manuale:", error);
      return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Toggle tra Chat e Problem Solver */}
      <div className="flex gap-2">
        <Button
          variant={!showProblemSolver ? "default" : "outline"}
          onClick={() => setShowProblemSolver(false)}
          className="flex-1"
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Chat Assistente
        </Button>
        <Button
          variant={showProblemSolver ? "default" : "outline"}
          onClick={() => setShowProblemSolver(true)}
          className="flex-1"
        >
          <HelpCircle className="h-4 w-4 mr-2" />
          Risolvi Problema
        </Button>
      </div>

      {!showProblemSolver ? (
        /* Chat Interface */
        <Card className="h-[600px] flex flex-col">
          <CardHeader className="pb-2 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="h-5 w-5 text-blue-600" />
              Assistente SmartBuilding
            </CardTitle>
            <CardDescription>
              Chiedimi informazioni sui tuoi elettrodomestici, consumi, manutenzioni...
            </CardDescription>
            {/* Selettore elettrodomestico opzionale */}
            <div className="mt-2">
              <Select
                value={selectedElettrodomestico}
                onValueChange={setSelectedElettrodomestico}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filtra per elettrodomestico (opzionale)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli elettrodomestici</SelectItem>
                  {elettrodomestici.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nome} - {e.marca} {e.modello}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Ciao! Come posso aiutarti?</p>
                  <div className="mt-4 space-y-2 text-sm">
                    <p className="text-gray-500">Prova a chiedermi:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {[
                        "Quanti elettrodomestici ho?",
                        "Quanto consumo al mese?",
                        "Quali manutenzioni ho in programma?"
                      ].map((suggestion, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          onClick={() => setInputMessage(suggestion)}
                          className="text-xs"
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === "user"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.sources && msg.sources.length > 0 && (
                          <div className="mt-2 flex gap-1">
                            {msg.sources.map((source, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {source}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {msg.suggested_actions && msg.suggested_actions.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {msg.suggested_actions.map((action, i) => (
                              <Button
                                key={i}
                                variant="outline"
                                size="sm"
                                className="text-xs bg-white"
                                onClick={() => {
                                  if (action.type === "navigate_matterport") {
                                    onNavigateToElettrodomestico?.(action.elettrodomestico_id);
                                  } else if (action.type === "open_ticket") {
                                    handleApriTicket();
                                  } else if (action.type === "contact_support") {
                                    handleContattaAssistenza();
                                  } else if (action.type === "navigate_tab") {
                                    onNavigateToTab?.(action.tab);
                                  }
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                        {/* Mostra pulsanti azione se il messaggio contiene parole chiave di problema non risolto */}
                        {msg.role === "assistant" && 
                         (msg.content?.toLowerCase().includes("non riesco") || 
                          msg.content?.toLowerCase().includes("contatta") ||
                          msg.content?.toLowerCase().includes("assistenza") ||
                          msg.content?.toLowerCase().includes("tecnico")) && 
                         selectedElettrodomestico && (
                          <div className="mt-3 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-2">Azioni rapide:</p>
                            <div className="flex flex-wrap gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={handleApriTicket}
                              >
                                🎫 Apri Ticket
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={handleContattaAssistenza}
                              >
                                📞 Assistenza
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={handleVaiNelloSpazio}
                              >
                                🏠 Vista 3D
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Scrivi un messaggio..."
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button onClick={sendMessage} disabled={isLoading || !inputMessage.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        /* Problem Solver Interface */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-orange-600" />
              Risolvi un Problema
            </CardTitle>
            <CardDescription>
              Descrivi il problema con un elettrodomestico e ti aiuterò a risolverlo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Elettrodomestico *</Label>
              <Select
                value={selectedElettrodomestico}
                onValueChange={setSelectedElettrodomestico}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona elettrodomestico" />
                </SelectTrigger>
                <SelectContent>
                  {elettrodomestici.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nome} - {e.marca} {e.modello}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrivi il problema *</Label>
              <Textarea
                value={problema}
                onChange={(e) => setProblema(e.target.value)}
                placeholder="Es: La lavatrice fa rumore durante la centrifuga..."
                rows={4}
              />
            </div>

            <Button
              onClick={risolviProblema}
              disabled={isLoading || !selectedElettrodomestico || !problema.trim()}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analisi in corso...
                </>
              ) : (
                <>
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Trova Soluzione
                </>
              )}
            </Button>

            {soluzione && (
              <div className="mt-4 space-y-4">
                <Separator />
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Soluzione per {soluzione.elettrodomestico?.nome}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {soluzione.elettrodomestico?.marca} {soluzione.elettrodomestico?.modello}
                  </p>
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border">
                      {soluzione.soluzione}
                    </pre>
                  </div>
                  {soluzione.centro_assistenza && (
                    <div className="mt-4 p-3 bg-blue-50 rounded flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-600" />
                      <span className="text-sm">{soluzione.centro_assistenza}</span>
                    </div>
                  )}
                </div>

                {/* Pulsanti Azione - Se il problema non è risolto */}
                {showActionButtons && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <h4 className="font-medium mb-3 flex items-center gap-2 text-orange-800">
                      <AlertCircle className="h-4 w-4" />
                      Il problema persiste? Ecco cosa puoi fare:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Button
                        variant="outline"
                        className="w-full border-orange-300 hover:bg-orange-100 text-orange-700"
                        onClick={handleApriTicket}
                      >
                        <Ticket className="h-4 w-4 mr-2" />
                        Apri Ticket
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-blue-300 hover:bg-blue-100 text-blue-700"
                        onClick={handleContattaAssistenza}
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Contatta Assistenza
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-purple-300 hover:bg-purple-100 text-purple-700"
                        onClick={handleVaiNelloSpazio}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Vai nello Spazio 3D
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ stats, consumiPerCategoria }) => {
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium opacity-90">Consumo Mensile</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.consumi?.consumo_mensile_kw || 0} kWh</div>
            <p className="text-xs opacity-75">~€{stats.consumi?.costo_stimato_mensile_euro || 0}/mese</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium opacity-90">Elettrodomestici</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.consumi?.numero_elettrodomestici || 0}</div>
            <p className="text-xs opacity-75">{stats.elettrodomestici_in_garanzia || 0} in garanzia</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium opacity-90">Manutenzioni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.manutenzioni_pianificate || 0}</div>
            <p className="text-xs opacity-75">{stats.manutenzioni_in_scadenza || 0} in scadenza</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium opacity-90">Costo Annuale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€{stats.consumi?.costo_stimato_annuale_euro || 0}</div>
            <p className="text-xs opacity-75">{stats.consumi?.consumo_annuale_kw || 0} kWh/anno</p>
          </CardContent>
        </Card>
      </div>

      {/* Consumi per Categoria */}
      {consumiPerCategoria && consumiPerCategoria.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Consumi per Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {consumiPerCategoria.map((cat) => {
                const catInfo = CATEGORIE_ELETTRODOMESTICI.find(c => c.value === cat.categoria);
                const maxConsumo = Math.max(...consumiPerCategoria.map(c => c.consumo_mensile_kw));
                const percentage = maxConsumo > 0 ? (cat.consumo_mensile_kw / maxConsumo) * 100 : 0;
                
                return (
                  <div key={cat.categoria} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{catInfo?.icon} {catInfo?.label || cat.categoria}</span>
                      <span className="font-medium">{cat.consumo_mensile_kw} kWh/mese</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">{cat.numero_elettrodomestici} elettrodomestici</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Centro Assistenza Form Dialog
const CentroAssistenzaDialog = ({ open, onOpenChange, centro, onSave }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    nome_azienda: "",
    referente: "",
    telefono: "",
    email: "",
    indirizzo: "",
    specializzazioni: [],
    note: "",
  });
  const [specializzazioneInput, setSpecializzazioneInput] = useState("");

  useEffect(() => {
    if (centro) {
      setFormData(centro);
    } else {
      setFormData({
        nome_azienda: "",
        referente: "",
        telefono: "",
        email: "",
        indirizzo: "",
        specializzazioni: [],
        note: "",
      });
    }
  }, [centro]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const addSpecializzazione = () => {
    if (specializzazioneInput.trim()) {
      setFormData({
        ...formData,
        specializzazioni: [...formData.specializzazioni, specializzazioneInput.trim()],
      });
      setSpecializzazioneInput("");
    }
  };

  const removeSpecializzazione = (index) => {
    setFormData({
      ...formData,
      specializzazioni: formData.specializzazioni.filter((_, i) => i !== index),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {centro ? "Modifica Centro Assistenza" : "Nuovo Centro Assistenza"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome_azienda">Nome Azienda *</Label>
            <Input
              id="nome_azienda"
              value={formData.nome_azienda}
              onChange={(e) => setFormData({ ...formData, nome_azienda: e.target.value })}
              required
              data-testid="centro-nome-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="referente">Referente</Label>
              <Input
                id="referente"
                value={formData.referente || ""}
                onChange={(e) => setFormData({ ...formData, referente: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefono">Telefono *</Label>
              <Input
                id="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                required
                data-testid="centro-telefono-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ""}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="indirizzo">Indirizzo</Label>
            <Input
              id="indirizzo"
              value={formData.indirizzo || ""}
              onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Specializzazioni</Label>
            <div className="flex gap-2">
              <Input
                value={specializzazioneInput}
                onChange={(e) => setSpecializzazioneInput(e.target.value)}
                placeholder="Aggiungi specializzazione"
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addSpecializzazione())}
              />
              <Button type="button" variant="outline" onClick={addSpecializzazione}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.specializzazioni.map((spec, index) => (
                <Badge key={index} variant="secondary" className="gap-1">
                  {spec}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => removeSpecializzazione(index)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note</Label>
            <Textarea
              id="note"
              value={formData.note || ""}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" data-testid="centro-save-btn">
              {centro ? t.common.save : t.common.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Manutenzione Form Dialog
// ============== DETTAGLIO MANUTENZIONE (Solo Lettura) ==============
const DettaglioManutenzioneDialog = ({ open, onOpenChange, manutenzione, elettrodomestici, centriAssistenza }) => {
  if (!manutenzione) return null;
  
  const elettro = elettrodomestici?.find(e => e.id === manutenzione.elettrodomestico_id);
  const centro = centriAssistenza?.find(c => c.id === manutenzione.centro_assistenza_id);
  const statoInfo = STATI_MANUTENZIONE.find(s => s.value === manutenzione.stato);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Dettaglio Manutenzione
            <Badge className={`${statoInfo?.color} text-white ml-2`}>
              {statoInfo?.label}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Visualizzazione dettagli manutenzione completata/annullata
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info Elettrodomestico */}
          {elettro && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-1">Elettrodomestico</h4>
              <p className="text-sm">{elettro.nome} - {elettro.marca} {elettro.modello}</p>
            </div>
          )}

          {/* Descrizione */}
          <div>
            <Label className="text-gray-500">Descrizione</Label>
            <p className="mt-1 p-2 bg-gray-50 rounded">{manutenzione.descrizione || "-"}</p>
          </div>

          {/* Date e Tipo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-500">Tipo</Label>
              <p className="mt-1 p-2 bg-gray-50 rounded capitalize">{manutenzione.tipo}</p>
            </div>
            <div>
              <Label className="text-gray-500">Costo</Label>
              <p className="mt-1 p-2 bg-gray-50 rounded">{manutenzione.costo ? `€${manutenzione.costo}` : "-"}</p>
            </div>
            <div>
              <Label className="text-gray-500">Data Programmata</Label>
              <p className="mt-1 p-2 bg-gray-50 rounded">{formatDateIT(manutenzione.data_programmata) || "-"}</p>
            </div>
            <div>
              <Label className="text-gray-500">Data Completamento</Label>
              <p className="mt-1 p-2 bg-gray-50 rounded">{formatDateIT(manutenzione.data_completamento) || "-"}</p>
            </div>
          </div>

          {/* Centro Assistenza */}
          {centro && (
            <div className="p-3 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-800 mb-1">Centro Assistenza</h4>
              <p className="text-sm">{centro.nome_azienda}</p>
              {centro.telefono && <p className="text-xs text-gray-600">📞 {centro.telefono}</p>}
            </div>
          )}

          {/* Note */}
          {manutenzione.note && (
            <div>
              <Label className="text-gray-500">Note</Label>
              <p className="mt-1 p-2 bg-gray-50 rounded whitespace-pre-wrap text-sm">{manutenzione.note}</p>
            </div>
          )}

          {/* Timestamp */}
          <div className="text-xs text-gray-400 pt-2 border-t">
            Creata: {formatDateIT(manutenzione.created_at)} | 
            Aggiornata: {formatDateIT(manutenzione.updated_at)}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== MANUTENZIONE DIALOG (Modifica) ==============
const ManutenzioneDialog = ({ open, onOpenChange, manutenzione, preselectedElettroId, elettrodomestici, centriAssistenza, onSave }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    elettrodomestico_id: "",
    tipo: "ordinaria",
    descrizione: "",
    data_programmata: "",
    data_completamento: "",
    stato: "aperto",
    costo: null,
    usa_centro_assistenza_elettrodomestico: true,
    centro_assistenza_id: "",
    ricorrente: false,
    frequenza_giorni: null,
    note: "",
    crea_ticket_automatico: false,
  });

  // Se manutenzione è completata/annullata, non permettere modifica
  const isReadOnly = manutenzione && (manutenzione.stato === "completato" || manutenzione.stato === "annullato");

  useEffect(() => {
    if (manutenzione) {
      setFormData({
        ...manutenzione,
        elettrodomestico_id: manutenzione.elettrodomestico_id || "",
        centro_assistenza_id: manutenzione.centro_assistenza_id || "",
        costo: manutenzione.costo || null,
        frequenza_giorni: manutenzione.frequenza_giorni || null,
        crea_ticket_automatico: false,
      });
    } else {
      setFormData({
        elettrodomestico_id: preselectedElettroId || "",
        tipo: "ordinaria",
        descrizione: "",
        data_programmata: "",
        data_completamento: "",
        stato: "aperto",
        costo: null,
        usa_centro_assistenza_elettrodomestico: true,
        centro_assistenza_id: "",
        ricorrente: false,
        frequenza_giorni: null,
        note: "",
        crea_ticket_automatico: false,
      });
    }
  }, [manutenzione, preselectedElettroId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSave = {
      ...formData,
      elettrodomestico_id: formData.elettrodomestico_id || null,
      centro_assistenza_id: formData.centro_assistenza_id || null,
    };
    onSave(dataToSave);
  };

  const selectedElettrodomestico = elettrodomestici.find(e => e.id === formData.elettrodomestico_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {manutenzione ? t.maintenance.editMaintenance : t.maintenance.addNew}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="elettrodomestico_id">Elettrodomestico</Label>
            <Select
              value={formData.elettrodomestico_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, elettrodomestico_id: value === "none" ? "" : value })}
            >
              <SelectTrigger data-testid="manut-elettro-select">
                <SelectValue placeholder="Seleziona elettrodomestico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuno (manutenzione generale)</SelectItem>
                {elettrodomestici.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.nome} - {e.posizione || "N/D"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => setFormData({ ...formData, tipo: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_MANUTENZIONE.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stato">Stato</Label>
              <Select
                value={formData.stato}
                onValueChange={(value) => setFormData({ ...formData, stato: value })}
              >
                <SelectTrigger data-testid="manut-stato-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATI_MANUTENZIONE.map((stato) => (
                    <SelectItem key={stato.value} value={stato.value}>
                      {stato.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="descrizione">Descrizione *</Label>
            <Textarea
              id="descrizione"
              value={formData.descrizione}
              onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
              required
              rows={3}
              data-testid="manut-descrizione-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="data_programmata">Data Programmata</Label>
              <Input
                id="data_programmata"
                type="date"
                value={formData.data_programmata || ""}
                onChange={(e) => setFormData({ ...formData, data_programmata: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_completamento">Data Completamento</Label>
              <Input
                id="data_completamento"
                type="date"
                value={formData.data_completamento || ""}
                onChange={(e) => setFormData({ ...formData, data_completamento: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="costo">Costo (€)</Label>
            <Input
              id="costo"
              type="text"
              placeholder="Es: 150.00 o da definire"
              value={formData.costo || ""}
              onChange={(e) => {
                const val = e.target.value;
                // Permetti input libero, converte in numero se possibile
                const numVal = parseFloat(val.replace(',', '.'));
                setFormData({ ...formData, costo: !isNaN(numVal) ? numVal : val || null });
              }}
            />
          </div>

          <Separator />

          <div className="space-y-4">
            <h4 className="font-medium">Centro Assistenza</h4>
            
            {selectedElettrodomestico?.centro_assistenza_id && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="usa_centro_assistenza_elettrodomestico"
                  checked={formData.usa_centro_assistenza_elettrodomestico}
                  onCheckedChange={(checked) => setFormData({ ...formData, usa_centro_assistenza_elettrodomestico: checked })}
                />
                <Label htmlFor="usa_centro_assistenza_elettrodomestico">
                  Usa centro assistenza dell'elettrodomestico
                </Label>
              </div>
            )}

            {(!formData.usa_centro_assistenza_elettrodomestico || !selectedElettrodomestico?.centro_assistenza_id) && (
              <div className="space-y-2">
                <Label htmlFor="centro_assistenza_id">Seleziona Centro Assistenza</Label>
                <Select
                  value={formData.centro_assistenza_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, centro_assistenza_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona centro assistenza" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {centriAssistenza.map((centro) => (
                      <SelectItem key={centro.id} value={centro.id}>
                        {centro.nome_azienda} - {centro.telefono}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="ricorrente"
                checked={formData.ricorrente}
                onCheckedChange={(checked) => setFormData({ ...formData, ricorrente: checked })}
              />
              <Label htmlFor="ricorrente">Manutenzione ricorrente</Label>
            </div>

            {formData.ricorrente && (
              <div className="space-y-2">
                <Label htmlFor="frequenza_giorni">Frequenza (giorni)</Label>
                <Input
                  id="frequenza_giorni"
                  type="number"
                  min="1"
                  value={formData.frequenza_giorni || ""}
                  onChange={(e) => setFormData({ ...formData, frequenza_giorni: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="es. 30, 90, 365..."
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note</Label>
            <Textarea
              id="note"
              value={formData.note || ""}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows={2}
            />
          </div>

          {/* Checkbox per creare ticket automatico */}
          {!manutenzione && formData.elettrodomestico_id && (
            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="crea_ticket"
                checked={formData.crea_ticket_automatico}
                onChange={(e) => setFormData({ ...formData, crea_ticket_automatico: e.target.checked })}
                className="rounded border-blue-300"
              />
              <Label htmlFor="crea_ticket" className="cursor-pointer text-blue-800">
                🎫 Crea automaticamente un ticket di assistenza per questa manutenzione
              </Label>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" data-testid="manut-save-btn">
              {manutenzione ? t.common.save : t.maintenance.addNew}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main App Component
function SmartDomoApp() {
  const { t, lang } = useLanguage();
  
  // date-fns locale mapping
  const dateFnsLocales = { it: itLocale, en: enUS, fr: frLocale, es: esLocale };
  const dateLocale = dateFnsLocales[lang] || itLocale;
  // Backend health state
  const [backendReady, setBackendReady] = useState(true);
  const [backendCheckCount, setBackendCheckCount] = useState(0);
  
  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem("smartdomo_token"));
  const [authLoading, setAuthLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState(() => {
    // Deep-link support: land directly on the panorami tab when ?panorama=<id> is in URL
    // so the Panorami360 component can pick it up on mount.
    try {
      if (typeof window !== "undefined") {
        const q = new URLSearchParams(window.location.search);
        if (q.has("panorama") || q.has("openTour") || q.get("section") === "panorami") {
          return "panorami360";
        }
      }
    } catch (_) { /* ignore */ }
    return "matterport";
  });
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);

  // Auto-open wallet dialog when returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("wallet_session") || params.get("wallet_cancel")) {
      setWalletDialogOpen(true);
    }
    // Listen for "open recharge" event from anywhere in the app
    const handler = () => setWalletDialogOpen(true);
    window.addEventListener("wallet:open-recharge", handler);
    return () => window.removeEventListener("wallet:open-recharge", handler);
  }, []);

  // Helper: check if a toggleable module is enabled for the current user (default true)
  const TOGGLEABLE_MODULE_IDS = ["matterport", "videocam", "sensori", "manutenzioni", "calendario", "elettrodomestici", "tickets", "veicoli", "assistente", "inventario", "dipendenti", "design", "computo", "panorami360"];
  const isModuleEnabled = (moduleId) => {
    if (!TOGGLEABLE_MODULE_IDS.includes(moduleId)) return true;
    const m = currentUser?.modules_enabled;
    if (!m) return true;
    return m[moduleId] !== false;
  };

  // Auto-redirect if activeTab is on a disabled module → fallback to "smartdomo" (always active)
  useEffect(() => {
    if (TOGGLEABLE_MODULE_IDS.includes(activeTab) && !isModuleEnabled(activeTab)) {
      setActiveTab("smartdomo");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, activeTab]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [consumiPerCategoria, setConsumiPerCategoria] = useState([]);
  
  // Data states
  const [elettrodomestici, setElettrodomestici] = useState([]);
  const [manutenzioni, setManutenzioni] = useState([]);
  const [centriAssistenza, setCentriAssistenza] = useState([]);
  const [categorieList, setCategorieList] = useState(CATEGORIE_ELETTRODOMESTICI); // Dynamic categories
  const [matterportPois, setMatterportPois] = useState([]); // POIs for appliance linking
  
  // Dialog states
  const [centroDialogOpen, setCentroDialogOpen] = useState(false);
  const [editingCentro, setEditingCentro] = useState(null);
  const [elettroDialogOpen, setElettroDialogOpen] = useState(false);
  const [editingElettro, setEditingElettro] = useState(null);
  const [manutDialogOpen, setManutDialogOpen] = useState(false);
  const [editingManut, setEditingManut] = useState(null);
  const [preselectedElettroId, setPreselectedElettroId] = useState(null); // For new maintenance from appliance card
  const [dettaglioManutOpen, setDettaglioManutOpen] = useState(false);
  const [viewingManut, setViewingManut] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrElettro, setQrElettro] = useState(null);
  const [ricercaCentriOpen, setRicercaCentriOpen] = useState(false);
  const [schedaApparatoOpen, setSchedaApparatoOpen] = useState(false);
  const [viewingApparato, setViewingApparato] = useState(null);
  const [apparatoManutenzioni, setApparatoManutenzioni] = useState([]);
  const [apparatoConsumi, setApparatoConsumi] = useState(null); // Consumption data from smart plug
  const [loadingConsumi, setLoadingConsumi] = useState(false);
  const [navigateToPoiId, setNavigateToPoiId] = useState(null); // POI to navigate to in 3D view
  
  // SmartThings devices for form
  const [smartThingsDevices, setSmartThingsDevices] = useState([]);
  const [smartThingsStates, setSmartThingsStates] = useState({}); // { deviceId: "on" | "off" }
  
  // Filter states
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroStatoManut, setFiltroStatoManut] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Auth functions
  const handleLogin = (user, token) => {
    setCurrentUser(user);
    setAuthToken(token);
    localStorage.setItem("smartdomo_token", token);
    localStorage.setItem("smartdomo_user", JSON.stringify(user));
  };

  const handleLogout = async () => {
    if (authToken) {
      try {
        await axios.post(`${API}/auth/logout?token=${authToken}`);
      } catch (e) {
        // Ignore logout errors
      }
    }
    setCurrentUser(null);
    setAuthToken(null);
    localStorage.removeItem("smartdomo_token");
    localStorage.removeItem("smartdomo_user");
    toast.success("Logout effettuato");
  };

  // Health check - verify backend is reachable.
  // Robust against transient timeouts during long-running AI requests:
  // - Higher timeout (10s) instead of 5s
  // - Require 3 CONSECUTIVE failures before flipping to splash screen
  // - Any successful check resets the counter immediately
  useEffect(() => {
    let healthCheckInterval = null;
    let consecutiveFailures = 0;
    const FAILURE_THRESHOLD = 3;

    const checkBackendHealth = async () => {
      try {
        await axios.get(`${API}/health`, { timeout: 10000 });
        // Success → backend reachable, reset failures
        consecutiveFailures = 0;
        setBackendReady(true);
        setBackendCheckCount(0);
      } catch (error) {
        if (error.response) {
          // Got an HTTP response (even 4xx/5xx) → backend is alive, just rejecting
          consecutiveFailures = 0;
          setBackendReady(true);
          setBackendCheckCount(0);
          return;
        }
        // Network / timeout error
        consecutiveFailures += 1;
        if (consecutiveFailures >= FAILURE_THRESHOLD) {
          setBackendReady(false);
          setBackendCheckCount(consecutiveFailures);
        }
      }
    };

    checkBackendHealth();
    healthCheckInterval = setInterval(checkBackendHealth, 20000);

    return () => {
      if (healthCheckInterval) clearInterval(healthCheckInterval);
    };
  }, []);

  // Verify session on mount
  useEffect(() => {
    const verifySession = async () => {
      const token = localStorage.getItem("smartdomo_token");
      if (!token) {
        try {
          await axios.post(`${API}/auth/init-admin`);
        } catch (e) {
          // Ignore
        }
        setAuthLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify?token=${token}`);
        if (response.data.valid) {
          setCurrentUser(response.data.user);
          setAuthToken(token);
        } else {
          // Session explicitly invalid - clear stored tokens
          localStorage.removeItem("smartdomo_token");
          localStorage.removeItem("smartdomo_user");
        }
      } catch (error) {
        // Only clear tokens on explicit 401/403 responses, not on network errors
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem("smartdomo_token");
          localStorage.removeItem("smartdomo_user");
        } else {
          // Network error or server error - keep tokens and try to use cached user
          const cachedUser = localStorage.getItem("smartdomo_user");
          if (cachedUser) {
            try {
              setCurrentUser(JSON.parse(cachedUser));
              setAuthToken(token);
            } catch (e) {
              // Invalid cached user
            }
          }
        }
      }
      setAuthLoading(false);
    };

    verifySession();
  }, []);

  // Load data functions
  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error("Error loading config:", error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const [statsRes, consumiRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { params: { token: authToken } }),
        axios.get(`${API}/dashboard/consumi-per-categoria`, { params: { token: authToken } }),
      ]);
      setStats(statsRes.data);
      setConsumiPerCategoria(consumiRes.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }, [authToken]);

  const loadCentriAssistenza = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/centri-assistenza`, { params: { token: authToken } });
      setCentriAssistenza(response.data);
    } catch (error) {
      console.error("Error loading centri assistenza:", error);
    }
  }, [authToken]);

  const loadCategorie = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categorie-all`, { params: { token: authToken } });
      if (response.data && response.data.length > 0) {
        // Transform to format expected by filter
        const dynamicCategories = response.data.map(cat => ({
          value: cat.id,
          label: cat.nome,
          icon: cat.custom ? "📋" : (CATEGORIE_ELETTRODOMESTICI.find(c => c.value === cat.id)?.icon || "📦"),
          custom: cat.custom || false
        }));
        setCategorieList(dynamicCategories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }, [authToken]);

  const loadElettrodomestici = useCallback(async () => {
    try {
      const params = { token: authToken };
      if (filtroCategoria) params.categoria = filtroCategoria;
      const response = await axios.get(`${API}/elettrodomestici`, { params });
      setElettrodomestici(response.data);
    } catch (error) {
      console.error("Error loading elettrodomestici:", error);
    }
  }, [filtroCategoria, authToken]);

  const loadSmartThingsDevices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/smartthings/devices`);
      const devices = response.data.devices || [];
      setSmartThingsDevices(devices);
      
      // Load states for switch devices
      const switchDevices = devices.filter(d => d.capabilities?.includes('switch'));
      const states = {};
      
      // Load in batches to avoid rate limiting
      const batchSize = 5;
      for (let i = 0; i < switchDevices.length; i += batchSize) {
        const batch = switchDevices.slice(i, i + batchSize);
        const promises = batch.map(async (device) => {
          try {
            const statusRes = await axios.get(`${API}/smartthings/device/${device.id}/status`);
            const switchState = statusRes.data?.components?.main?.switch?.switch?.value;
            if (switchState) {
              states[device.id] = switchState;
            }
          } catch (e) {
            // Ignore individual device errors
          }
        });
        await Promise.all(promises);
        if (i + batchSize < switchDevices.length) {
          await new Promise(r => setTimeout(r, 300));
        }
      }
      setSmartThingsStates(states);
    } catch (error) {
      console.log("SmartThings not available:", error.message);
    }
  }, []);

  const loadManutenzioni = useCallback(async () => {
    try {
      const params = { token: authToken };
      if (filtroStatoManut) params.stato = filtroStatoManut;
      const response = await axios.get(`${API}/manutenzioni`, { params });
      setManutenzioni(response.data);
    } catch (error) {
      console.error("Error loading manutenzioni:", error);
    }
  }, [filtroStatoManut, authToken]);

  // Load Matterport POIs for appliance linking
  const loadMatterportPois = useCallback(async () => {
    try {
      const params = authToken ? { token: authToken } : {};
      const response = await axios.get(`${API}/matterport/pois`, { params });
      console.log(`Loaded ${response.data.length} Matterport POIs`);
      setMatterportPois(response.data);
    } catch (error) {
      console.error("Error loading Matterport POIs:", error);
    }
  }, [authToken]);

  // Reload POIs when auth token changes (after login)
  useEffect(() => {
    if (authToken) {
      loadMatterportPois();
    }
  }, [authToken, loadMatterportPois]);

  // Initial load
  useEffect(() => {
    loadConfig();
    loadCentriAssistenza();
    loadSmartThingsDevices();
    loadCategorie();
    loadMatterportPois();
  }, [loadConfig, loadCentriAssistenza, loadSmartThingsDevices, loadCategorie, loadMatterportPois]);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === "dashboard") {
      loadStats();
    } else if (activeTab === "smartdomo") {
      loadElettrodomestici();
      loadManutenzioni();
    } else if (activeTab === "elettrodomestici") {
      loadElettrodomestici();
      loadCategorie(); // Refresh categories when viewing elettrodomestici
    } else if (activeTab === "manutenzioni") {
      loadManutenzioni();
      loadElettrodomestici();
    } else if (activeTab === "assistente") {
      loadElettrodomestici();
    } else if (activeTab === "tickets") {
      loadElettrodomestici();
      loadCentriAssistenza();
    } else if (activeTab === "planimetria") {
      loadElettrodomestici();
    }
  }, [activeTab, loadStats, loadElettrodomestici, loadManutenzioni, loadCentriAssistenza, loadCategorie]);

  // CRUD handlers for Centri Assistenza
  const handleSaveCentro = async (data) => {
    try {
      if (editingCentro) {
        await axios.put(`${API}/centri-assistenza/${editingCentro.id}`, data);
        toast.success("Centro assistenza aggiornato");
      } else {
        await axios.post(`${API}/centri-assistenza`, data);
        toast.success("Centro assistenza creato");
      }
      setCentroDialogOpen(false);
      setEditingCentro(null);
      loadCentriAssistenza();
    } catch (error) {
      toast.error("Errore nel salvataggio");
      console.error(error);
    }
  };

  const handleDeleteCentro = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo centro assistenza?")) {
      try {
        await axios.delete(`${API}/centri-assistenza/${id}`);
        toast.success("Centro assistenza eliminato");
        loadCentriAssistenza();
      } catch (error) {
        toast.error("Errore nell'eliminazione");
        console.error(error);
      }
    }
  };

  // Handler per aggiungere centro dalla ricerca web
  const handleAddCentroFromSearch = (centroData) => {
    setEditingCentro(centroData);
    setCentroDialogOpen(true);
    setRicercaCentriOpen(false);
  };

  // CRUD handlers for Elettrodomestici
  // Navigate to POI in 3D view
  const handleNavigateToPoi = (poiId) => {
    if (!poiId) {
      toast.error("Questo apparato non ha un POI associato");
      return;
    }
    setNavigateToPoiId(poiId);
    setActiveTab("matterport");
    toast.info("Navigazione verso il punto 3D...");
  };

  // Open scheda apparato with manutenzioni
  const handleOpenSchedaApparato = async (apparato) => {
    setViewingApparato(apparato);
    setSchedaApparatoOpen(true);
    setApparatoConsumi(null); // Reset consumption data
    
    // Load manutenzioni for this apparato
    try {
      const response = await axios.get(`${API}/manutenzioni`, {
        params: { token: authToken, elettrodomestico_id: apparato.id }
      });
      setApparatoManutenzioni(response.data);
    } catch (error) {
      console.error("Error loading manutenzioni:", error);
      setApparatoManutenzioni([]);
    }
    
    // Load consumption data if smart plug is connected
    const smartDeviceId = apparato.smart_plug_id || apparato.smartthings_device_id;
    if (smartDeviceId) {
      loadApparatoConsumi(smartDeviceId);
    }
  };
  
  // Load consumption data for an appliance's smart plug
  const loadApparatoConsumi = async (deviceId) => {
    setLoadingConsumi(true);
    try {
      const response = await axios.get(`${API}/device/${deviceId}/consumption`);
      setApparatoConsumi(response.data);
    } catch (error) {
      console.error("Error loading consumption data:", error);
      setApparatoConsumi(null);
    } finally {
      setLoadingConsumi(false);
    }
  };

  const handleSaveElettro = async (data) => {
    try {
      console.log("Saving elettrodomestico:", data);
      if (editingElettro) {
        await axios.put(`${API}/elettrodomestici/${editingElettro.id}?token=${authToken}`, data);
        toast.success("Elettrodomestico aggiornato");
      } else {
        await axios.post(`${API}/elettrodomestici?token=${authToken}`, data);
        toast.success("Elettrodomestico creato");
      }
      setElettroDialogOpen(false);
      setEditingElettro(null);
      loadElettrodomestici();
      loadStats();
    } catch (error) {
      console.error("Errore salvataggio elettrodomestico:", error);
      console.error("Response:", error.response?.data);
      
      // Handle different error formats
      let errorMsg = "Errore nel salvataggio";
      const detail = error.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors array
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        // Single error object
        errorMsg = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      toast.error(errorMsg);
    }
  };

  const handleDeleteElettro = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo elettrodomestico?")) {
      try {
        await axios.delete(`${API}/elettrodomestici/${id}?token=${authToken}`);
        toast.success("Elettrodomestico eliminato");
        loadElettrodomestici();
        loadStats();
      } catch (error) {
        toast.error("Errore nell'eliminazione");
        console.error(error);
      }
    }
  };

  // CRUD handlers for Manutenzioni
  const handleSaveManut = async (data) => {
    try {
      if (editingManut) {
        await axios.put(`${API}/manutenzioni/${editingManut.id}?token=${authToken}`, data);
        toast.success("Manutenzione aggiornata");
      } else {
        await axios.post(`${API}/manutenzioni?token=${authToken}`, data);
        toast.success("Manutenzione creata");
      }
      setManutDialogOpen(false);
      setEditingManut(null);
      setPreselectedElettroId(null);
      loadManutenzioni();
      loadStats();
    } catch (error) {
      toast.error("Errore nel salvataggio");
      console.error(error);
    }
  };

  const handleDeleteManut = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questa manutenzione?")) {
      try {
        await axios.delete(`${API}/manutenzioni/${id}?token=${authToken}`);
        toast.success("Manutenzione eliminata");
        loadManutenzioni();
        loadStats();
      } catch (error) {
        toast.error("Errore nell'eliminazione");
        console.error(error);
      }
    }
  };

  // Filter elettrodomestici by search
  const filteredElettrodomestici = elettrodomestici.filter(e => 
    !searchTerm || 
    e.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.posizione?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading while verifying auth
  if (authLoading) {
    return (
      <div translate="no" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Show loading screen while backend is starting up
  if (!backendReady) {
    return (
      <div translate="no" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-6">
            <Building2 className="h-16 w-16 text-blue-400 mx-auto animate-pulse" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">SmartDomo</h1>
          <p className="text-blue-300 mb-6">Avvio servizi in corso...</p>
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
            <span className="text-slate-400 text-sm">
              Connessione al server ({backendCheckCount})
            </span>
          </div>
          <p className="text-slate-500 text-xs mt-4 mb-4">
            Attendere qualche secondo...
          </p>
          {backendCheckCount >= 3 && (
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm transition-colors"
            >
              Ricarica Pagina
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!currentUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div translate="no" className="min-h-screen bg-gray-50">
      
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="w-full px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo e titolo */}
            <div className="flex items-center gap-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{t.header.title}</h1>
                <p className="text-xs text-gray-500">{t.header.subtitle}</p>
              </div>
            </div>
            
            {/* Language selector + User info + logout */}
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <NotificationBadge onClick={() => setActiveTab("suggerimenti")} authToken={authToken} />
              <WalletWidget authToken={authToken} onClickRecharge={() => setWalletDialogOpen(true)} />
              <div className="flex items-center gap-3 pl-3 border-l">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-700">{currentUser.full_name || currentUser.username}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                    {currentUser.role === "admin" && <Shield className="h-3 w-3" />}
                    {currentUser.role === "admin" ? t.header.admin : t.header.user}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-500 hover:text-red-500"
                  title={t.header.logout}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation - Grouped Tabs */}
      <nav className="bg-white border-b shadow-sm">
        <div className="w-full px-4">
          <div className="flex items-end gap-0.5 overflow-x-auto py-1">
            {/* Gruppo 1: Digital Twin & Design - Indigo */}
            {(() => {
              const dtTabs = [
                { id: "matterport", label: t.nav.vista3d, icon: Eye, core: false },
                { id: "panorami360", label: "Panorami 360°", icon: Globe, core: false },
                { id: "inventario", label: "Inventario", icon: Package, core: false },
                { id: "design", label: "AI Design", icon: Sparkles, core: false },
                { id: "computo", label: "Computo", icon: Calculator, core: false },
              ].filter(tab => tab.core || isModuleEnabled(tab.id));
              if (dtTabs.length === 0) return null;
              return (
                <div className="flex flex-col mr-1.5">
                  <span className="text-[10px] font-bold tracking-wider text-indigo-700/80 uppercase pl-2 leading-none mb-0.5">Digital Twin</span>
                  <div className="flex items-center bg-indigo-50 rounded-lg px-1">
                    {dtTabs.map((tab) => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? "bg-indigo-600 text-white shadow-sm"
                            : "text-indigo-700 hover:bg-indigo-100"
                        }`} data-testid={`nav-${tab.id}`}>
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Gruppo 2: Domotica - Amber */}
            {(() => {
              const domTabs = [
                { id: "smartdomo", label: t.nav.smartdomo, icon: Thermometer, core: true },
                { id: "sensori", label: t.nav.reportSensori, icon: Activity, core: false },
                { id: "videocam", label: t.nav.videocam, icon: Video, core: false },
                { id: "elettrodomestici", label: t.nav.apparati, icon: Zap, core: false },
                { id: "assistente", label: t.nav.assistente, icon: Bot, core: false },
              ].filter(tab => tab.core || isModuleEnabled(tab.id));
              if (domTabs.length === 0) return null;
              return (
                <div className="flex flex-col mr-1.5">
                  <span className="text-[10px] font-bold tracking-wider text-amber-700/80 uppercase pl-2 leading-none mb-0.5">Domotica</span>
                  <div className="flex items-center bg-amber-50 rounded-lg px-1">
                    {domTabs.map((tab) => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? "bg-amber-600 text-white shadow-sm"
                            : "text-amber-700 hover:bg-amber-100"
                        }`} data-testid={`nav-${tab.id}`}>
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Gruppo 3: Manutenzioni & Operatori - Rose */}
            {(() => {
              const mantTabs = [
                { id: "manutenzioni", label: t.nav.manutenzioni, icon: Wrench, core: false },
                { id: "calendario", label: t.nav.calendario, icon: Calendar, core: false },
                { id: "tickets", label: t.nav.ticket, icon: Ticket, core: false },
                { id: "veicoli", label: t.nav.veicoli, icon: Car, core: false },
                { id: "dipendenti", label: "Operatori Manutenzioni", icon: Users, core: false },
              ].filter(tab => tab.core || isModuleEnabled(tab.id));
              if (mantTabs.length === 0) return null;
              return (
                <div className="flex flex-col mr-1.5">
                  <span className="text-[10px] font-bold tracking-wider text-rose-700/80 uppercase pl-2 leading-none mb-0.5">Manutenzioni</span>
                  <div className="flex items-center bg-rose-50 rounded-lg px-1">
                    {mantTabs.map((tab) => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? "bg-rose-600 text-white shadow-sm"
                            : "text-rose-700 hover:bg-rose-100"
                        }`} data-testid={`nav-${tab.id}`}>
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Setup - Slate */}
            <div className="flex flex-col">
              <span className="text-[10px] font-bold tracking-wider text-slate-600/80 uppercase pl-2 leading-none mb-0.5">Setup</span>
              <div className="flex items-center bg-slate-100 rounded-lg px-1">
                <button onClick={() => setActiveTab("proprieta")}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                    activeTab === "proprieta"
                      ? "bg-slate-700 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-200"
                  }`} data-testid="nav-proprieta">
                  <Settings className="h-4 w-4" />
                  {t.nav.setup}
                </button>
                {currentUser?.role === "admin" && (
                  <button onClick={() => setActiveTab("utenti")}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                      activeTab === "utenti"
                        ? "bg-slate-700 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-200"
                    }`} data-testid="nav-utenti">
                    <Users className="h-4 w-4" />
                    {t.nav.utenti}
                  </button>
                )}
                {currentUser?.role === "admin" && (
                  <button onClick={() => setActiveTab("wallet-admin")}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                      activeTab === "wallet-admin"
                        ? "bg-slate-700 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-200"
                    }`} data-testid="nav-wallet-admin">
                    <Coins className="h-4 w-4" />
                    Wallet
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      {/* SmartDomo Tab - New Full Interface */}
      {activeTab === "smartdomo" && (
        <SmartBuildingDashboard 
          onNavigate={setActiveTab}
          manutenzioni={manutenzioni}
          elettrodomestici={elettrodomestici}
          currentUser={currentUser}
        />
      )}

      <main className={`${activeTab === 'smartdomo' || activeTab === 'matterport' ? 'hidden' : 'max-w-7xl mx-auto px-4 py-6'}`}>
        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <Dashboard stats={stats} consumiPerCategoria={consumiPerCategoria} />
        )}

        {/* Proprietà Tab - ora Setup */}
        {activeTab === "proprieta" && (
          <PropertyConfig currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken} />
        )}

        {/* Report Sensori Tab */}
        {activeTab === "sensori" && (
          <SensorReport authToken={authToken} />
        )}

        {/* Veicoli GPS Tab */}
        {activeTab === "veicoli" && (
          <VehicleTracker authToken={authToken} />
        )}

        {/* User Management Tab - Admin only */}
        {activeTab === "utenti" && currentUser?.role === "admin" && (
          <UserManagement 
            currentUser={currentUser} 
            token={authToken}
            onUserUpdate={() => {}}
          />
        )}

        {/* Suggerimenti Tab */}
        {activeTab === "suggerimenti" && (
          <SuggerimentiProattivi 
            onNavigateToElettrodomestico={(id) => {
              setActiveTab("elettrodomestici");
            }}
            authToken={authToken}
          />
        )}

        {/* Inventario Tab */}
        {activeTab === "inventario" && (
          <Inventario authToken={authToken} matterportPois={matterportPois}
            onNavigateToPoi={(poiId) => { setActiveTab("matterport"); setNavigateToPoiId(poiId); }} />
        )}

        {/* Dipendenti Tab */}
        {activeTab === "dipendenti" && (
          <Dipendenti authToken={authToken} />
        )}

        {/* AI Design Assistant Tab */}
        {activeTab === "design" && (
          <DesignAssistant authToken={authToken} />
        )}

        {/* Computo Metrico Tab */}
        {activeTab === "computo" && (
          <ComputoMetrico authToken={authToken} />
        )}

        {/* Panorami 360° Tab */}
        {activeTab === "panorami360" && (
          <Panorami360 authToken={authToken} />
        )}

        {/* Wallet Admin Tab */}
        {activeTab === "wallet-admin" && currentUser?.role === "admin" && (
          <WalletAdminPanel authToken={authToken} />
        )}

        {/* Assistente AI Tab */}
        {activeTab === "assistente" && (
          <AssistenteAI 
            elettrodomestici={elettrodomestici}
            authToken={authToken}
            onNavigateToElettrodomestico={(id) => {
              setActiveTab("matterport");
              // Qui potremmo navigare al tag specifico quando avremo SDK Matterport
            }}
            onOpenTicket={(ticketData) => {
              // Apre la tab ticket e prepara i dati per un nuovo ticket
              setActiveTab("tickets");
              // Potremmo anche settare uno stato per pre-popolare il form
              toast.info(`Apri un nuovo ticket per: ${ticketData.elettrodomestico?.nome || 'elettrodomestico'}`);
            }}
            onNavigateToTab={(tab) => {
              setActiveTab(tab);
            }}
          />
        )}

        {/* Calendario Tab */}
        {activeTab === "calendario" && (
          <CalendarioManutenzioni />
        )}

        {/* Tickets Tab */}
        {activeTab === "tickets" && (
          <TicketList 
            elettrodomestici={elettrodomestici} 
            centriAssistenza={centriAssistenza}
          />
        )}

        {/* Planimetria Tab */}
        {activeTab === "planimetria" && (
          <PlanimetriaEditor 
            elettrodomestici={elettrodomestici}
            onElettrodomesticoClick={(elettro) => {
              setEditingElettro(elettro);
              setElettroDialogOpen(true);
            }}
            authToken={authToken}
          />
        )}

        {/* Elettrodomestici Tab */}
        {activeTab === "elettrodomestici" && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex flex-1 gap-4 items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t.common.search + "..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="elettro-search"
                  />
                </div>
                <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={t.common.category} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    {categorieList.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label} {cat.custom && <span className="text-xs text-blue-500">(custom)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => {
                  setEditingElettro(null);
                  setElettroDialogOpen(true);
                }}
                data-testid="add-elettro-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Elettrodomestico
              </Button>
            </div>

            {/* Elettrodomestici Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredElettrodomestici.map((e) => {
                const catInfo = categorieList.find(c => c.value === e.categoria) || CATEGORIE_ELETTRODOMESTICI.find(c => c.value === e.categoria);
                const hasSmartPlug = e.smart_plug_id && e.smart_plug_provider !== "nessuno";
                const smartState = hasSmartPlug ? smartThingsStates[e.smart_plug_id] : null;
                
                return (
                  <Card key={e.id} className="hover:shadow-md transition-shadow" data-testid={`elettro-card-${e.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{catInfo?.icon || "📦"}</span>
                          <div>
                            <CardTitle className="text-base">{e.nome}</CardTitle>
                            <CardDescription>{e.marca} {e.modello}</CardDescription>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {/* Badge POI 3D - mostra se l'apparato ha un tag Matterport collegato */}
                          {e.matterport_tag_id && (
                            <Badge variant="outline" className="gap-1 bg-cyan-50 text-cyan-700 border-cyan-300">
                              <Box className="h-3 w-3" />
                              POI 3D
                            </Badge>
                          )}
                          {hasSmartPlug && (
                            <>
                              <Badge variant="outline" className="gap-1">
                                <Plug className="h-3 w-3" />
                                Smart
                              </Badge>
                              {smartState && (
                                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  smartState === 'on' 
                                    ? 'bg-red-100 text-red-700' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  <div className={`w-2 h-2 rounded-full ${
                                    smartState === 'on' 
                                      ? 'bg-red-500 animate-pulse' 
                                      : 'bg-gray-400'
                                  }`} />
                                  {smartState === 'on' ? 'ACCESO' : 'SPENTO'}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {e.posizione && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {e.posizione}
                        </p>
                      )}
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-gray-500 text-xs">Consumo/mese</p>
                          <p className="font-bold text-blue-600">{e.consumo_mensile_kw} kWh</p>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <p className="text-gray-500 text-xs">Costo/mese</p>
                          <p className="font-bold text-green-600">€{(e.consumo_mensile_kw * 0.25).toFixed(2)}</p>
                        </div>
                      </div>

                      {e.centro_assistenza && (
                        <div className="text-sm bg-gray-50 p-2 rounded">
                          <p className="text-gray-500 text-xs flex items-center gap-1">
                            <Users className="h-3 w-3" /> Centro Assistenza
                          </p>
                          <p className="font-medium">{e.centro_assistenza.nome_azienda}</p>
                          <p className="text-gray-600 flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {e.centro_assistenza.telefono}
                          </p>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleOpenSchedaApparato(e)}
                        >
                          <Eye className="h-3 w-3 mr-1" /> Apri Scheda
                        </Button>
                        {e.matterport_tag_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                            onClick={() => handleNavigateToPoi(e.matterport_tag_id)}
                            title="Vai al POI nella Vista 3D"
                          >
                            <Navigation className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setQrElettro(e);
                            setQrDialogOpen(true);
                          }}
                          title="Genera QR Code"
                        >
                          <QrCode className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteElettro(e.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredElettrodomestici.length === 0 && (
              <Card className="p-8 text-center">
                <Zap className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nessun elettrodomestico trovato</p>
                <Button
                  className="mt-4"
                  onClick={() => {
                    setEditingElettro(null);
                    setElettroDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi il primo
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* Manutenzioni Tab */}
        {activeTab === "manutenzioni" && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <Select value={filtroStatoManut} onValueChange={setFiltroStatoManut}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {STATI_MANUTENZIONE.map((stato) => (
                    <SelectItem key={stato.value} value={stato.value}>
                      {stato.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  setEditingManut(null);
                  setManutDialogOpen(true);
                }}
                data-testid="add-manut-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t.maintenance.addNew}
              </Button>
            </div>

            {/* Manutenzioni List */}
            <div className="space-y-3">
              {manutenzioni.map((m) => {
                const statoInfo = STATI_MANUTENZIONE.find(s => s.value === m.stato);
                const tipoInfo = TIPI_MANUTENZIONE.find(t => t.value === m.tipo);
                
                return (
                  <Card key={m.id} className="hover:shadow-md transition-shadow" data-testid={`manut-card-${m.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={statoInfo?.color}>
                              {statoInfo?.label}
                            </Badge>
                            <Badge variant="outline">{tipoInfo?.label}</Badge>
                            {m.ricorrente && (
                              <Badge variant="secondary">Ricorrente ({m.frequenza_giorni}gg)</Badge>
                            )}
                          </div>
                          
                          <p className="font-medium">{m.descrizione}</p>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            {m.elettrodomestico && (
                              <span className="flex items-center gap-1">
                                <Zap className="h-3 w-3" /> {m.elettrodomestico.nome}
                              </span>
                            )}
                            {m.data_programmata && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> {formatDateIT(m.data_programmata)}
                              </span>
                            )}
                            {m.costo && (
                              <span className="flex items-center gap-1">
                                <Euro className="h-3 w-3" /> {m.costo}€
                              </span>
                            )}
                          </div>

                          {m.centro_assistenza && (
                            <div className="text-sm bg-gray-50 p-2 rounded inline-flex items-center gap-2">
                              <Users className="h-4 w-4 text-gray-400" />
                              <span>{m.centro_assistenza.nome_azienda}</span>
                              <span className="text-gray-400">|</span>
                              <Phone className="h-3 w-3 text-gray-400" />
                              <span>{m.centro_assistenza.telefono}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {/* Se completata/annullata: solo visualizza */}
                          {(m.stato === "completato" || m.stato === "annullato") ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setViewingManut(m);
                                setDettaglioManutOpen(true);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" /> Visualizza
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingManut(m);
                                  setManutDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteManut(m.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {manutenzioni.length === 0 && (
              <Card className="p-8 text-center">
                <Wrench className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nessuna manutenzione trovata</p>
                <Button
                  className="mt-4"
                  onClick={() => {
                    setEditingManut(null);
                    setManutDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi la prima
                </Button>
              </Card>
            )}
          </div>
        )}

        {/* Centri Assistenza Tab */}
        {activeTab === "centri" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setRicercaCentriOpen(true)}
              >
                <Search className="h-4 w-4 mr-2" />
                Cerca sul Web
              </Button>
              <Button
                onClick={() => {
                  setEditingCentro(null);
                  setCentroDialogOpen(true);
                }}
                data-testid="add-centro-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Centro
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {centriAssistenza.map((centro) => (
                <Card key={centro.id} className="hover:shadow-md transition-shadow" data-testid={`centro-card-${centro.id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      {centro.nome_azienda}
                    </CardTitle>
                    {centro.referente && (
                      <CardDescription>{centro.referente}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <a href={`tel:${centro.telefono}`} className="text-blue-600 hover:underline">
                        {centro.telefono}
                      </a>
                    </p>
                    {centro.email && (
                      <p className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <a href={`mailto:${centro.email}`} className="text-blue-600 hover:underline">
                          {centro.email}
                        </a>
                      </p>
                    )}
                    {centro.indirizzo && (
                      <p className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {centro.indirizzo}
                      </p>
                    )}
                    {centro.specializzazioni.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-2">
                        {centro.specializzazioni.map((spec, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {spec}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setEditingCentro(centro);
                          setCentroDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Modifica
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteCentro(centro.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {centriAssistenza.length === 0 && (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nessun centro assistenza trovato</p>
                <Button
                  className="mt-4"
                  onClick={() => {
                    setEditingCentro(null);
                    setCentroDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi il primo
                </Button>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Wallet recharge dialog (globally accessible) */}
      <WalletRechargeDialog open={walletDialogOpen} onOpenChange={setWalletDialogOpen} authToken={authToken} />

      {/* Matterport Tab - Full width outside main. Always mounted to avoid reloading the 3D space iframe every time the user returns to this tab. */}
      <div style={{ display: activeTab === "matterport" ? "block" : "none" }}>
        <MatterportManager 
          authToken={authToken} 
          currentUser={currentUser}
          navigateToPoiId={navigateToPoiId}
          onNavigationComplete={() => setNavigateToPoiId(null)}
        />
      </div>

      {activeTab === "videocam" && (
        <VideoCameraManager 
          authToken={authToken} 
          currentUser={currentUser}
          matterportPois={matterportPois}
        />
      )}

      {/* Dialogs */}
      <CentroAssistenzaDialog
        open={centroDialogOpen}
        onOpenChange={setCentroDialogOpen}
        centro={editingCentro}
        onSave={handleSaveCentro}
      />

      <ElettrodomesticoDialog
        open={elettroDialogOpen}
        onOpenChange={setElettroDialogOpen}
        elettrodomestico={editingElettro}
        centriAssistenza={centriAssistenza}
        smartThingsDevices={smartThingsDevices}
        pois={matterportPois}
        onSave={handleSaveElettro}
      />

      {/* Scheda Apparato Dialog */}
      <Dialog open={schedaApparatoOpen} onOpenChange={setSchedaApparatoOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              {viewingApparato?.nome || "Scheda Apparato"}
            </DialogTitle>
          </DialogHeader>
          
          {viewingApparato && (
            <Tabs defaultValue="manutenzioni" className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="manutenzioni" className="flex-1">
                  <Wrench className="h-4 w-4 mr-2" />
                  Manutenzioni ({apparatoManutenzioni.length})
                </TabsTrigger>
                {/* Show Consumi tab only if smart plug is connected */}
                {(viewingApparato.smart_plug_id || viewingApparato.smartthings_device_id) && (
                  <TabsTrigger value="consumi" className="flex-1">
                    <Activity className="h-4 w-4 mr-2" />
                    Consumi
                  </TabsTrigger>
                )}
                <TabsTrigger value="dettagli" className="flex-1">
                  <FileText className="h-4 w-4 mr-2" />
                  Dettagli
                </TabsTrigger>
                <TabsTrigger value="documenti" className="flex-1">
                  <Upload className="h-4 w-4 mr-2" />
                  Documenti
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="manutenzioni" className="mt-4">
                {/* Riepilogo */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{apparatoManutenzioni.length}</p>
                      <p className="text-sm text-blue-700">Interventi Totali</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {apparatoManutenzioni.filter(m => m.stato === 'completata').length}
                      </p>
                      <p className="text-sm text-green-700">Completate</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        €{apparatoManutenzioni.reduce((sum, m) => sum + (m.costo || 0), 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-amber-700">Totale Speso</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Lista Manutenzioni */}
                {apparatoManutenzioni.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Wrench className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>{t.maintenance.noMaintenance}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {apparatoManutenzioni.map(m => (
                      <div key={m.id} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant={m.stato === 'completata' ? 'default' : m.stato === 'in_corso' ? 'secondary' : 'outline'}>
                                {m.stato}
                              </Badge>
                              <Badge variant="outline">{m.tipo}</Badge>
                            </div>
                            <p className="font-medium mt-1">{m.descrizione}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {m.data_programmata ? format(parseISO(m.data_programmata), "dd/MM/yyyy", { locale: dateLocale }) : "N/D"}
                              </span>
                              {m.tecnico && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {m.tecnico}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {m.costo > 0 && (
                              <p className="font-bold text-green-600">€{m.costo.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                        {m.note && (
                          <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">{m.note}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add maintenance button */}
                <Button
                  className="w-full mt-4"
                  variant="outline"
                  onClick={() => {
                    setSchedaApparatoOpen(false);
                    setEditingManut(null);
                    setPreselectedElettroId(viewingApparato.id);
                    setManutDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t.maintenance.addNew}
                </Button>
              </TabsContent>
              
              {/* Tab Consumi - shown only if smart plug is connected */}
              {(viewingApparato.smart_plug_id || viewingApparato.smartthings_device_id) && (
                <TabsContent value="consumi" className="mt-4">
                  {loadingConsumi ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                      <span className="ml-2 text-gray-500">Caricamento dati...</span>
                    </div>
                  ) : apparatoConsumi?.has_consumption_data ? (
                    <div className="space-y-4">
                      {/* Device Info */}
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                        <div className="flex items-center gap-2">
                          <Plug className="h-5 w-5 text-amber-500" />
                          <div>
                            <p className="font-medium">{apparatoConsumi.device_name}</p>
                            <p className="text-xs text-gray-500">Smart Plug collegato</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${apparatoConsumi.online ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className={`text-sm font-medium ${apparatoConsumi.online ? 'text-green-600' : 'text-red-600'}`}>
                            {apparatoConsumi.online ? 'Online' : 'Offline'}
                          </span>
                          {apparatoConsumi.switch && (
                            <Badge variant={apparatoConsumi.switch === 'on' ? 'default' : 'secondary'}>
                              {apparatoConsumi.switch === 'on' ? 'Acceso' : 'Spento'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Real-time Power Data */}
                      <div className="grid grid-cols-3 gap-4">
                        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                          <CardContent className="p-4 text-center">
                            <Zap className="h-6 w-6 text-amber-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-amber-600">
                              {apparatoConsumi.power_w?.toFixed(1) || '0'} W
                            </p>
                            <p className="text-sm text-amber-700">Potenza attuale</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                          <CardContent className="p-4 text-center">
                            <Activity className="h-6 w-6 text-blue-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-blue-600">
                              {apparatoConsumi.voltage_v?.toFixed(0) || '0'} V
                            </p>
                            <p className="text-sm text-blue-700">Tensione</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                          <CardContent className="p-4 text-center">
                            <TrendingUp className="h-6 w-6 text-purple-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold text-purple-600">
                              {apparatoConsumi.current_a?.toFixed(2) || '0'} A
                            </p>
                            <p className="text-sm text-purple-700">Corrente</p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Consumption Summary */}
                      <div className="grid grid-cols-2 gap-4">
                        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-green-700 font-medium">Consumo Oggi</p>
                                <p className="text-3xl font-bold text-green-600">
                                  {apparatoConsumi.daily_kwh || 0} kWh
                                </p>
                                {apparatoConsumi.daily_cost_eur && (
                                  <p className="text-sm text-green-600 mt-1">
                                    ≈ €{apparatoConsumi.daily_cost_eur.toFixed(2)}
                                  </p>
                                )}
                              </div>
                              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                <Calendar className="h-6 w-6 text-green-500" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-cyan-700 font-medium">Consumo Mese</p>
                                <p className="text-3xl font-bold text-cyan-600">
                                  {apparatoConsumi.monthly_kwh || 0} kWh
                                </p>
                                {apparatoConsumi.monthly_cost_eur && (
                                  <p className="text-sm text-cyan-600 mt-1">
                                    ≈ €{apparatoConsumi.monthly_cost_eur.toFixed(2)}
                                  </p>
                                )}
                              </div>
                              <div className="h-12 w-12 rounded-full bg-cyan-100 flex items-center justify-center">
                                <BarChart3 className="h-6 w-6 text-cyan-500" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Refresh Button */}
                      <Button
                        className="w-full"
                        variant="outline"
                        onClick={() => loadApparatoConsumi(viewingApparato.smart_plug_id || viewingApparato.smartthings_device_id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Aggiorna Dati
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Plug className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>Nessun dato di consumo disponibile</p>
                      <p className="text-sm mt-1">Il dispositivo smart collegato potrebbe non supportare la misurazione dei consumi</p>
                    </div>
                  )}
                </TabsContent>
              )}
              
              <TabsContent value="dettagli" className="mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">{t.appliances.brand}</Label>
                    <p className="font-medium">{viewingApparato.marca || "N/D"}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">{t.appliances.model}</Label>
                    <p className="font-medium">{viewingApparato.modello || "N/D"}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">{t.common.category}</Label>
                    <p className="font-medium">{viewingApparato.categoria}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">{t.appliances.position}</Label>
                    <p className="font-medium">{viewingApparato.ubicazione || "N/D"}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">{t.appliances.purchaseDate}</Label>
                    <p className="font-medium">
                      {viewingApparato.data_acquisto 
                        ? format(parseISO(viewingApparato.data_acquisto), "dd/MM/yyyy", { locale: dateLocale })
                        : "N/D"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500">{t.appliances.warrantyExpiry}</Label>
                    <p className="font-medium">
                      {viewingApparato.scadenza_garanzia 
                        ? format(parseISO(viewingApparato.scadenza_garanzia), "dd/MM/yyyy", { locale: dateLocale })
                        : "N/D"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-500">{t.appliances.serialNumber}</Label>
                    <p className="font-medium font-mono">{viewingApparato.seriale || "N/D"}</p>
                  </div>
                </div>
                
                <Button
                  className="w-full mt-4"
                  variant="outline"
                  onClick={() => {
                    setSchedaApparatoOpen(false);
                    setEditingElettro(viewingApparato);
                    setElettroDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {t.common.edit}
                </Button>
              </TabsContent>
              
              <TabsContent value="documenti" className="mt-4">
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Nessun documento allegato</p>
                  <Button className="mt-4" variant="outline" disabled>
                    <Upload className="h-4 w-4 mr-2" />
                    Carica Documento
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <ManutenzioneDialog
        open={manutDialogOpen}
        onOpenChange={(open) => {
          setManutDialogOpen(open);
          if (!open) setPreselectedElettroId(null);
        }}
        manutenzione={editingManut}
        preselectedElettroId={preselectedElettroId}
        elettrodomestici={elettrodomestici}
        centriAssistenza={centriAssistenza}
        onSave={handleSaveManut}
      />

      <DettaglioManutenzioneDialog
        open={dettaglioManutOpen}
        onOpenChange={setDettaglioManutOpen}
        manutenzione={viewingManut}
        elettrodomestici={elettrodomestici}
        centriAssistenza={centriAssistenza}
      />

      <QRCodeDialog
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        elettrodomestico={qrElettro}
      />

      <RicercaCentriAssistenza
        open={ricercaCentriOpen}
        onOpenChange={setRicercaCentriOpen}
        onAddCentro={handleAddCentroFromSearch}
      />
      
      {/* Logo Trivor overlay - covers platform watermark */}
      <div 
        className="fixed pointer-events-none flex items-center justify-end"
        style={{ 
          bottom: '0px',
          right: '0px',
          background: '#09090B',
          padding: '8px 16px',
          minWidth: '210px',
          borderTopLeftRadius: '6px',
          zIndex: 99999999
        }}
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_3837386c-e4f5-4acc-83b7-9dabadeaf885/artifacts/3sr9x015_logo%20trivor%20per%20copertura%20emergent.png"
          alt="Trivor"
          style={{ height: '26px', width: 'auto' }}
        />
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Toaster position="top-right" richColors />
      <SmartDomoApp />
    </LanguageProvider>
  );
}

export default App;
